import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Runs a callback once a CSS transition finishes on the given element,
 * with a timeout fallback in case the transitionend event never fires
 * (e.g. display change interrupts it, or duration is 0).
 * @param {Element | null | undefined} element
 * @param {() => void} callback
 * @param {string} [propertyName]
 */
function onTransitionEnd(element, callback, propertyName = 'transform') {
  if (!element) {
    callback();
    return;
  }

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    element.removeEventListener('transitionend', handler);
    clearTimeout(fallback);
    callback();
  };
  const handler = (event) => {
    if (event.target !== element) return;
    if (propertyName && event.propertyName !== propertyName) return;
    finish();
  };

  element.addEventListener('transitionend', handler);

  const durations = getComputedStyle(element)
    .transitionDuration.split(',')
    .map((d) => parseFloat(d) * 1000 || 0);
  const maxDuration = Math.max(0, ...durations);
  const fallback = setTimeout(finish, maxDuration + 50);
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Force-close any submenu that isn't an ancestor/descendant of the one we're
    // opening. This runs synchronously (no transition) to guarantee only one
    // submenu panel is ever "open" at a time - prevents overlapping panels
    // when a user taps quickly between menu items on mobile.
    this.querySelectorAll('.menu-drawer__menu-container[open]').forEach((openDetails) => {
      if (openDetails === details) return;
      if (openDetails.contains(details) || details.contains(openDetails)) return;

      openDetails.classList.remove('menu-open');
      openDetails.removeAttribute('open');
      openDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      this.#syncTopLevelVisibility();

      // Wait for the drawer transition to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onTransitionEnd(drawer || details, () => trapFocus(details));
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.querySelectorAll('.menu-drawer__menu-container[open]').forEach((item) => {
      item.classList.remove('menu-open');
      item.removeAttribute('open');

      const summary = item.querySelector('summary');

      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
      }
    });

    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.#syncTopLevelVisibility();

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onTransitionEnd(drawer || details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
      this.#syncTopLevelVisibility();
    });
  }

  /**
   * Keeps the top-level menu list in sync with whichever submenu (if any)
   * is currently open, entirely in JS so it can't be overridden by CSS
   * animations (e.g. Horizon's own .menu-drawer__animated-element entrance
   * animation, which also sets `visibility` and otherwise wins the cascade
   * over a plain stylesheet rule).
   *
   * - No submenu open: everything visible, normal main menu.
   * - A submenu open: every top-level row is hidden EXCEPT the one whose
   *   submenu is open - and even that row's own summary/title is hidden,
   *   since the open submenu panel now covers that space. This matches the
   *   original drawer UI where opening a submenu fully replaces the main
   *   menu instead of leaving any row peeking through.
   */
  #syncTopLevelVisibility() {
    const topList = this.refs.menuDrawer.querySelector('.menu-drawer__menu.has-submenu');
    if (!topList) return;

    const submenuOpen = this.refs.menuDrawer.classList.contains('menu-drawer--has-submenu-opened');

    topList.querySelectorAll(':scope > .menu-drawer__list-item').forEach((li) => {
      const openDetails = li.querySelector(':scope > details.menu-open');
      const summary = li.querySelector(':scope > details > summary');

      if (!submenuOpen) {
        li.style.removeProperty('visibility');
        if (summary) summary.style.removeProperty('visibility');
        return;
      }

      if (openDetails) {
        li.style.removeProperty('visibility');
        if (summary) summary.style.setProperty('visibility', 'hidden', 'important');
      } else {
        li.style.setProperty('visibility', 'hidden', 'important');
        if (summary) summary.style.removeProperty('visibility');
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');

  element.querySelectorAll('.menu-drawer__menu-container').forEach((submenu) => {
    submenu.classList.remove('menu-open');
    submenu.removeAttribute('open');

    submenu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  });
}